import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faEdit, faTrash, faMinusCircle, faRandom, faSpinner, faFolderPlus, faFolder } from '@fortawesome/free-solid-svg-icons';
import Constant from '../Constant';
import { useToast } from '../../hooks/useToast';
import { getAccessToken } from '../../hooks/AccessToken';



const AppManagement = () => {
  const [popups, setPopups] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showTestInfoModal, setShowTestInfoModal] = useState(false);
  const [editingPopup, setEditingPopup] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [activeGroupMenuId, setActiveGroupMenuId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    text: '',
    duration: 'once',
    frequencyType: 'once',
    frequencyValue: 1,
    route: '/',
    icon: '',
    customDate: '',
    isTest: false,
    testUserId: '',
    groupId: ''
  });
  const [groupFormData, setGroupFormData] = useState({
    name: '',
    description: '',
    frequency: 'once',
    frequencyType: 'once',
    frequencyValue: 1,
    cooldownDays: 0,
    customDate: '',
    isTest: false,
    testUserId: '',
    isActive: true
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingGroup, setSavingGroup] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [sendingGroupId, setSendingGroupId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const fileInputRef = useRef(null);

  const { toast, ToastContainer } = useToast();

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    console.log("adding check iff thiw comes ")
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ========================
  // DATA FETCHING
  // ========================

  const fetchPopups = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${Constant.apiBasePath}popups/get`);
      const data = await response.json();
      setPopups(data);
    } catch (error) {
      toast.error('Failed to fetch popups');
      console.error('Error fetching popups:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const token = getAccessToken();
      const response = await fetch(`${Constant.apiBasePath}popups/groups`, {
        headers: { token }
      });
      const data = await response.json();
      setGroups(data);
    } catch (error) {
      toast.error('Failed to fetch groups');
      console.error('Error fetching groups:', error);
    }
  };

  useEffect(() => {
    fetchPopups();
    fetchGroups();
  }, []);

  // ========================
  // FREQUENCY HELPERS
  // ========================

  // Parse a duration string like 'once', 'every_3_days', 'every_2_weeks' into type + value
  const parseFrequency = (duration) => {
    if (!duration || duration === 'once') {
      return { frequencyType: 'once', frequencyValue: 1 };
    }
    const daysMatch = duration.match(/every_(\d+)_days/);
    if (daysMatch) {
      return { frequencyType: 'days', frequencyValue: parseInt(daysMatch[1]) };
    }
    const weeksMatch = duration.match(/every_(\d+)_weeks/);
    if (weeksMatch) {
      return { frequencyType: 'weeks', frequencyValue: parseInt(weeksMatch[1]) };
    }
    return { frequencyType: 'once', frequencyValue: 1 };
  };

  // Compose a duration string from type + value
  const composeFrequency = (type, value) => {
    if (type === 'once') return 'once';
    if (type === 'days') return `every_${value}_days`;
    if (type === 'weeks') return `every_${value}_weeks`;
    return 'once';
  };

  // ========================
  // POPUP (NOTIFICATION) CRUD
  // ========================

  const resetForm = () => {
    setFormData({
      title: '',
      text: '',
      duration: 'once',
      frequencyType: 'once',
      frequencyValue: 1,
      route: '/',
      icon: '',
      customDate: '',
      isTest: false,
      testUserId: '',
      isActive: true,
      groupId: ''
    });
    setImageFile(null);
    setImagePreview('');
    setEditingPopup(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const togglePopup = (popup = null) => {
    if (popup) {
      const { frequencyType, frequencyValue } = parseFrequency(popup.duration);
      setEditingPopup(popup);
      setFormData({
        title: popup.title,
        text: popup.text,
        duration: popup.duration,
        frequencyType,
        frequencyValue,
        route: popup.route || '/',
        icon: popup.icon || '',
        customDate: popup.customDate ? popup.customDate.slice(0, 16) : '',
        isTest: popup.isTest || false,
        testUserId: popup.testUserId || '',
        isActive: popup.isActive !== undefined ? popup.isActive : true,
        groupId: popup.groupId || ''
      });
      setImagePreview(popup.image || '');
      setImageFile(null);
    } else {
      resetForm();
    }
    setShowPopup(!showPopup);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFrequencyTypeChange = (e) => {
    const type = e.target.value;
    setFormData(prev => ({
      ...prev,
      frequencyType: type,
      frequencyValue: type === 'once' ? 1 : prev.frequencyValue,
      duration: composeFrequency(type, type === 'once' ? 1 : prev.frequencyValue)
    }));
  };

  const handleFrequencyValueChange = (e) => {
    const val = parseInt(e.target.value) || 1;
    const maxVal = formData.frequencyType === 'weeks' ? 52 : 365;
    const value = Math.max(1, Math.min(maxVal, val));
    setFormData(prev => ({
      ...prev,
      frequencyValue: value,
      duration: composeFrequency(prev.frequencyType, value)
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleTestToggle = () => {
    setFormData(prev => ({
      ...prev,
      isTest: !prev.isTest,
      testUserId: !prev.isTest ? prev.testUserId : ''
    }));
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.text.trim()) {
      toast.warning('Title and text are required');
      return;
    }

    if (formData.isTest && !formData.testUserId.trim()) {
      toast.warning('User ID is required for test popups');
      return;
    }

    setSaving(true);

    try {
      const token = getAccessToken();
      const payload = new FormData();
      payload.append('title', formData.title);
      payload.append('text', formData.text);
      payload.append('duration', formData.duration);
      payload.append('route', formData.route);
      payload.append('icon', formData.icon);
      payload.append('isTest', formData.isTest);
      if (formData.isTest) {
        payload.append('testUserId', formData.testUserId);
      }
      payload.append('isActive', formData.isActive);
      payload.append('groupId', formData.groupId || '');
      if (formData.customDate) {
        payload.append('customDate', new Date(formData.customDate).toISOString());
      }
      if (imageFile) {
        payload.append('notificationImage', imageFile);
      }

      let response;
      if (editingPopup) {
        response = await fetch(`${Constant.apiBasePath}popups/${editingPopup._id}`, {
          method: 'PUT',
          headers: { token },
          body: payload,
        });
      } else {
        response = await fetch(`${Constant.apiBasePath}popups/`, {
          method: 'POST',
          headers: { token },
          body: payload,
        });
      }

      if (response.ok) {
        const updatedPopup = await response.json();

        if (editingPopup) {
          setPopups(prev => prev.map(p => p._id === editingPopup._id ? updatedPopup : p));
          toast.success('Notification updated successfully!');
        } else {
          setPopups(prev => [...prev, updatedPopup]);
          toast.success('Notification created successfully!');
        }

        // Refresh groups to update notification counts
        fetchGroups();
        togglePopup();
      } else {
        toast.error('Failed to save notification');
      }
    } catch (error) {
      toast.error('Error saving notification');
      console.error('Error saving notification:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (popupId) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) {
      return;
    }

    try {
      const token = getAccessToken();
      const response = await fetch(`${Constant.apiBasePath}popups/${popupId}`, {
        method: 'DELETE',
        headers: { token },
      });

      if (response.ok) {
        setPopups(prev => prev.filter(p => p._id !== popupId));
        fetchGroups(); // Refresh group notification counts
        toast.success('Notification deleted successfully!');
      } else {
        toast.error('Failed to delete notification');
      }
    } catch (error) {
      toast.error('Error deleting notification');
      console.error('Error deleting notification:', error);
    }
  };

  const handleSend = async (popupId) => {
    setSendingId(popupId);
    try {
      const token = getAccessToken();
      const response = await fetch(`${Constant.apiBasePath}popups/send/${popupId}`, {
        method: 'POST',
        headers: { token },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Notification sent successfully!');
      } else {
        toast.error('Failed to send notification');
      }
    } catch (error) {
      toast.error('Error sending notification');
      console.error('Error sending notification:', error);
    } finally {
      setSendingId(null);
    }
  };

  // ========================
  // GROUP CRUD
  // ========================

  const resetGroupForm = () => {
    setGroupFormData({
      name: '',
      description: '',
      frequency: 'once',
      frequencyType: 'once',
      frequencyValue: 1,
      cooldownDays: 0,
      customDate: '',
      isTest: false,
      testUserId: '',
      isActive: true
    });
    setEditingGroup(null);
  };

  const toggleGroupModal = (group = null) => {
    if (group) {
      const { frequencyType, frequencyValue } = parseFrequency(group.frequency);
      setEditingGroup(group);
      setGroupFormData({
        name: group.name,
        description: group.description || '',
        frequency: group.frequency,
        frequencyType,
        frequencyValue,
        cooldownDays: group.cooldownDays || 0,
        customDate: group.customDate ? group.customDate.slice(0, 16) : '',
        isTest: group.isTest || false,
        testUserId: group.testUserId || '',
        isActive: group.isActive !== undefined ? group.isActive : true
      });
    } else {
      resetGroupForm();
    }
    setShowGroupModal(!showGroupModal);
  };

  const handleGroupInputChange = (e) => {
    const { name, value } = e.target;
    setGroupFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGroupFrequencyTypeChange = (e) => {
    const type = e.target.value;
    setGroupFormData(prev => ({
      ...prev,
      frequencyType: type,
      frequencyValue: type === 'once' ? 1 : prev.frequencyValue,
      frequency: composeFrequency(type, type === 'once' ? 1 : prev.frequencyValue)
    }));
  };

  const handleGroupFrequencyValueChange = (e) => {
    const value = Math.max(1, Math.min(365, parseInt(e.target.value) || 1));
    setGroupFormData(prev => ({
      ...prev,
      frequencyValue: value,
      frequency: composeFrequency(prev.frequencyType, value)
    }));
  };

  const handleGroupTestToggle = () => {
    setGroupFormData(prev => ({
      ...prev,
      isTest: !prev.isTest,
      testUserId: !prev.isTest ? prev.testUserId : ''
    }));
  };

  const handleGroupSave = async () => {
    if (!groupFormData.name.trim()) {
      toast.warning('Group name is required');
      return;
    }

    if (groupFormData.isTest && !groupFormData.testUserId.trim()) {
      toast.warning('User ID is required for test groups');
      return;
    }

    setSavingGroup(true);

    try {
      const token = getAccessToken();
      const body = {
        name: groupFormData.name,
        description: groupFormData.description,
        frequency: groupFormData.frequency,
        cooldownDays: groupFormData.cooldownDays,
        isTest: groupFormData.isTest,
        testUserId: groupFormData.testUserId,
        isActive: groupFormData.isActive
      };
      if (groupFormData.customDate) {
        body.customDate = new Date(groupFormData.customDate).toISOString();
      }

      let response;
      if (editingGroup) {
        response = await fetch(`${Constant.apiBasePath}popups/groups/${editingGroup._id}`, {
          method: 'PUT',
          headers: { token, 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } else {
        response = await fetch(`${Constant.apiBasePath}popups/groups`, {
          method: 'POST',
          headers: { token, 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      }

      if (response.ok) {
        toast.success(editingGroup ? 'Group updated!' : 'Group created!');
        fetchGroups();
        toggleGroupModal();
      } else {
        toast.error('Failed to save group');
      }
    } catch (error) {
      toast.error('Error saving group');
      console.error('Error saving group:', error);
    } finally {
      setSavingGroup(false);
    }
  };

  const handleGroupDelete = async (groupId) => {
    if (!window.confirm('Delete this group? Member notifications will become standalone.')) {
      return;
    }

    try {
      const token = getAccessToken();
      const response = await fetch(`${Constant.apiBasePath}popups/groups/${groupId}`, {
        method: 'DELETE',
        headers: { token },
      });

      if (response.ok) {
        fetchGroups();
        fetchPopups(); // Refresh popups since their groupId was cleared
        toast.success('Group deleted!');
      } else {
        toast.error('Failed to delete group');
      }
    } catch (error) {
      toast.error('Error deleting group');
    }
  };

  const handleGroupSend = async (groupId) => {
    setSendingGroupId(groupId);
    try {
      const token = getAccessToken();
      const response = await fetch(`${Constant.apiBasePath}popups/groups/${groupId}/send`, {
        method: 'POST',
        headers: { token },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(`Sent: "${data.sentNotification?.title}"`);
        fetchGroups();
      } else {
        toast.error(data.error || 'Failed to send group notification');
      }
    } catch (error) {
      toast.error('Error sending group notification');
    } finally {
      setSendingGroupId(null);
    }
  };

  const handleRemoveFromGroup = async (notificationId, groupId) => {
    try {
      const token = getAccessToken();
      const response = await fetch(`${Constant.apiBasePath}popups/groups/${groupId}/remove`, {
        method: 'POST',
        headers: { token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ notificationId }),
      });

      if (response.ok) {
        toast.success('Notification removed from group');
        fetchGroups();
        fetchPopups();
      } else {
        toast.error('Failed to remove from group');
      }
    } catch (error) {
      toast.error('Error removing from group');
    }
  };

  const handleAddToGroup = async (notificationId, groupId) => {
    try {
      const token = getAccessToken();
      const response = await fetch(`${Constant.apiBasePath}popups/groups/${groupId}/add`, {
        method: 'POST',
        headers: { token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ notificationId }),
      });

      if (response.ok) {
        toast.success('Notification added to group');
        fetchGroups();
        fetchPopups();
      } else {
        toast.error('Failed to add to group');
      }
    } catch (error) {
      toast.error('Error adding to group');
    }
  };

  const toggleGroupExpand = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatFrequencyLabel = (duration) => {
    if (!duration || duration === 'once') return 'Once';
    const daysMatch = duration.match(/every_(\d+)_days/);
    if (daysMatch) return `Every ${daysMatch[1]} Day${daysMatch[1] > 1 ? 's' : ''}`;
    const weeksMatch = duration.match(/every_(\d+)_weeks/);
    if (weeksMatch) return `Every ${weeksMatch[1]} Week${weeksMatch[1] > 1 ? 's' : ''}`;
    return duration;
  };

  // Get only standalone (ungrouped) popups
  const standalonePopups = popups.filter(p => !p.groupId);

  // ========================
  // STYLES
  // ========================

  const getPopupContainerStyle = () => ({
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: isMobile ? "center" : "center",
    padding: isMobile ? "10px" : "0",
    overflowY: isMobile ? "auto" : "visible",
    zIndex: 1000,
  });

  const getPopupStyle = () => ({
    backgroundColor: "white",
    padding: isMobile ? "15px" : "20px",
    borderRadius: "10px",
    width: isMobile ? "100%" : "450px",
    maxWidth: isMobile ? "100%" : "450px",
    maxHeight: isMobile ? "80vh" : "85vh",
    overflow: "scroll",
    margin: isMobile ? "20px 0" : "0",
    fontSize: isMobile ? "14px" : "16px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  });

  const getInputStyle = () => ({
    width: "100%",
    padding: isMobile ? "8px" : "10px",
    marginBottom: isMobile ? "8px" : "10px",
    borderRadius: "5px",
    border: "1px solid #ddd",
    fontSize: isMobile ? "14px" : "16px",
    boxSizing: "border-box",
  });

  const getTextareaStyle = () => ({
    width: "100%",
    padding: isMobile ? "8px" : "10px",
    marginBottom: isMobile ? "8px" : "10px",
    borderRadius: "5px",
    border: "1px solid #ddd",
    height: isMobile ? "80px" : "100px",
    fontSize: isMobile ? "14px" : "16px",
    resize: "vertical",
    boxSizing: "border-box",
  });

  const getButtonGroupStyle = () => ({
    marginTop: isMobile ? "15px" : "10px",
    display: "flex",
    gap: isMobile ? "8px" : "10px",
    flexDirection: isMobile ? "column" : "row",
    alignItems: "center",
  });

  const getButtonStyle = (type) => ({
    padding: isMobile ? "10px 15px" : "8px 16px",
    fontSize: isMobile ? "14px" : "16px",
    borderRadius: "5px",
    border: "none",
    cursor: "pointer",
    flex: isMobile ? "1" : "none",
    minHeight: isMobile ? "44px" : "auto",
  });

  const getIconButton = (color, disabled = false) => ({
    background: "none",
    border: "none",
    padding: "4px 8px",
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: "16px",
    color: color,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: disabled ? 0.5 : 1,
    transition: "opacity 0.2s"
  });

  const getLabelStyle = () => ({
    display: "block",
    marginBottom: "5px",
    fontWeight: "500",
    color: "#333",
    fontSize: isMobile ? "13px" : "14px"
  });

  // ========================
  // FREQUENCY INPUT COMPONENT
  // ========================

  const FrequencyInput = ({ frequencyType, frequencyValue, onTypeChange, onValueChange, inputStyle, showGroupOverrideTooltip = false }) => (
    <div>
      <label style={{ ...getLabelStyle(), display: "flex", alignItems: "center", gap: "5px" }}>
        Frequency *
        {showGroupOverrideTooltip && (
          <span title="This notification belongs to a group. The group's frequency setting will override this." style={{ cursor: "help", color: "#6f42c1", fontSize: "14px", fontWeight: "bold" }}>
            ⓘ
          </span>
        )}
      </label>
      <div style={{ display: "flex", gap: "8px", marginBottom: isMobile ? "8px" : "10px" }}>
        <select
          value={frequencyType}
          onChange={onTypeChange}
          style={{ ...inputStyle, flex: "1", marginBottom: 0 }}
        >
          <option value="once">Once</option>
          <option value="days">Every X Days</option>
          <option value="weeks">Every X Weeks</option>
        </select>
        {frequencyType !== 'once' && (
          <input
            type="number"
            min="1"
            max={frequencyType === 'weeks' ? 52 : 365}
            value={frequencyValue}
            onChange={onValueChange}
            style={{ ...inputStyle, width: "80px", marginBottom: 0 }}
            placeholder={frequencyType === 'weeks' ? 'Weeks' : 'Days'}
          />
        )}
      </div>
      {frequencyType !== 'once' && (
        <small style={{ color: "#888", fontSize: "12px" }}>
          Notification will repeat every {frequencyValue} {frequencyType === 'weeks' ? `week${frequencyValue > 1 ? 's' : ''}` : `day${frequencyValue > 1 ? 's' : ''}`}
        </small>
      )}
    </div>
  );

  // ========================
  // RENDER
  // ========================

  return (
    <div className="content-page" style={{ padding: "20px" }}>
      <ToastContainer />

      <div className="header-common" style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "30px",
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? "15px" : "0"
      }}>
        <div>
          <h4 style={{ fontSize: isMobile ? "18px" : "24px", margin: 0 }}>Notification Management</h4>
        </div>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <button
            className="btn btn-secondary"
            onClick={() => setShowTestInfoModal(true)}
            style={{
              padding: isMobile ? "8px 16px" : "10px 20px",
              fontSize: isMobile ? "14px" : "16px",
              backgroundColor: "#6c757d",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer"
            }}
          >
            Test Info
          </button>
          <button
            className="btn btn-info"
            onClick={() => toggleGroupModal()}
            style={{
              padding: isMobile ? "8px 16px" : "10px 20px",
              fontSize: isMobile ? "14px" : "16px",
              backgroundColor: "#6f42c1",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer"
            }}
          >
            Create Group
          </button>
          <button
            className="btn btn-info"
            onClick={() => togglePopup()}
            style={{
              padding: isMobile ? "8px 16px" : "10px 20px",
              fontSize: isMobile ? "14px" : "16px",
              backgroundColor: "#17a2b8",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer"
            }}
          >
            Create Notification
          </button>
        </div>
      </div>

      {/* ======================== */}
      {/* NOTIFICATION MODAL */}
      {/* ======================== */}
      {showPopup && (
        <div style={getPopupContainerStyle()}>
          <div style={getPopupStyle()}>
            <h4 style={{
              fontSize: isMobile ? "16px" : "18px",
              marginBottom: isMobile ? "15px" : "20px",
              textAlign: "center",
            }}>
              {editingPopup ? "Edit Notification" : "Create New Notification"}
            </h4>

            <div>
              <label style={getLabelStyle()}>
                Title *
              </label>
              <input
                type="text"
                name="title"
                placeholder="Enter notification title"
                value={formData.title}
                onChange={handleInputChange}
                style={getInputStyle()}
              />
            </div>

            <div>
              <label style={getLabelStyle()}>
                Text *
              </label>
              <textarea
                name="text"
                placeholder="Enter notification text"
                value={formData.text}
                onChange={handleInputChange}
                style={getTextareaStyle()}
              />
            </div>

            {/* Frequency Input */}
            <FrequencyInput
              frequencyType={formData.frequencyType}
              frequencyValue={formData.frequencyValue}
              onTypeChange={handleFrequencyTypeChange}
              onValueChange={handleFrequencyValueChange}
              inputStyle={getInputStyle()}
              showGroupOverrideTooltip={!!formData.groupId}
            />

            {/* Group Assignment */}
            <div>
              <label style={getLabelStyle()}>
                Group (optional)
              </label>
              <select
                name="groupId"
                value={formData.groupId}
                onChange={handleInputChange}
                style={getInputStyle()}
              >
                <option value="">Standalone (No Group)</option>
                {groups.map(g => (
                  <option key={g._id} value={g._id}>{g.name}</option>
                ))}
              </select>
              {formData.groupId && (
                <small style={{ color: "#6f42c1", fontSize: "12px" }}>
                  This notification's frequency will be ignored — the group's frequency applies instead.
                </small>
              )}
            </div>

            <div>
              <label style={getLabelStyle()}>
                Status *
              </label>
              <select
                name="isActive"
                value={formData.isActive ? "true" : "false"}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.value === "true" }))}
                style={getInputStyle()}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            <div>
              <label style={getLabelStyle()}>
                Route
              </label>
              <input
                type="text"
                name="route"
                placeholder="e.g., /home, /dashboard"
                value={formData.route}
                onChange={handleInputChange}
                style={getInputStyle()}
              />
            </div>

            {/* Image Upload */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <label style={{ ...getLabelStyle(), marginBottom: 0 }}>
                  Notification Image
                </label>
                {imagePreview && (
                  <button
                    onClick={handleRemoveImage}
                    style={{
                      backgroundColor: "rgba(220, 53, 69, 0.9)",
                      color: "white",
                      border: "none",
                      borderRadius: "50%",
                      width: "24px",
                      height: "24px",
                      cursor: "pointer",
                      fontSize: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      lineHeight: "1",
                    }}
                    title="Remove image"
                  >
                    ✕
                  </button>
                )}
              </div>
              {!imagePreview && (
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/jpg,image/jpeg,image/png,image/webp"
                  onChange={handleImageChange}
                  style={{
                    ...getInputStyle(),
                    padding: "8px",
                    backgroundColor: "#f8f9fa",
                  }}
                />
              )}
              {imagePreview && (
                <div style={{
                  marginBottom: "10px",
                  display: "inline-block",
                }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "150px",
                      borderRadius: "5px",
                      border: "1px solid #ddd",
                      objectFit: "cover",
                    }}
                  />
                </div>
              )}
            </div>

            <div>
              <label style={{ ...getLabelStyle(), display: "flex", alignItems: "center", gap: "5px" }}>
                Custom Date
                {formData.groupId && (
                  <span title="This notification belongs to a group. The group's custom date setting will override this." style={{ cursor: "help", color: "#6f42c1", fontSize: "14px", fontWeight: "bold" }}>
                    ⓘ
                  </span>
                )}
              </label>
              <input
                type="datetime-local"
                name="customDate"
                value={formData.customDate}
                onChange={handleInputChange}
                style={getInputStyle()}
              />
            </div>

            {/* Test User ID field - shown when test mode is active */}
            {formData.isTest && (
              <div style={{
                padding: "12px",
                backgroundColor: "#fff3cd",
                borderRadius: "5px",
                border: "1px solid #ffc107",
                marginBottom: "10px",
              }}>
                <label style={{
                  ...getLabelStyle(),
                  color: "#856404",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}>
                  Test User ID *
                </label>
                <input
                  type="text"
                  name="testUserId"
                  placeholder="Enter MongoDB userId"
                  value={formData.testUserId}
                  onChange={handleInputChange}
                  style={{
                    ...getInputStyle(),
                    marginBottom: "0",
                    borderColor: "#ffc107",
                  }}
                />
                <small style={{ color: "#856404", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  Only this user will receive the notification
                </small>
              </div>
            )}

            <div style={getButtonGroupStyle()}>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  ...getButtonStyle('success'),
                  backgroundColor: "#28a745",
                  color: "white"
                }}
              >
                {saving ? 'Saving...' : (editingPopup ? 'Update' : 'Create')}
              </button>
              <button
                onClick={() => togglePopup()}
                style={{
                  ...getButtonStyle('danger'),
                  backgroundColor: "#dc3545",
                  color: "white"
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleTestToggle}
                style={{
                  ...getButtonStyle('test'),
                  backgroundColor: formData.isTest ? "#ffc107" : "#6c757d",
                  color: formData.isTest ? "#333" : "white",
                  marginLeft: isMobile ? "0" : "auto",
                  fontWeight: formData.isTest ? "600" : "normal",
                }}
                title={formData.isTest ? "Disable test mode" : "Enable test mode - send only to a specific user"}
              >
                {formData.isTest ? "Test On" : "Test"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================== */}
      {/* GROUP MODAL */}
      {/* ======================== */}
      {showGroupModal && (
        <div style={getPopupContainerStyle()}>
          <div style={getPopupStyle()}>
            <h4 style={{
              fontSize: isMobile ? "16px" : "18px",
              marginBottom: isMobile ? "15px" : "20px",
              textAlign: "center",
            }}>
              {editingGroup ? "Edit Group" : "Create Notification Group"}
            </h4>

            <div>
              <label style={getLabelStyle()}>Group Name *</label>
              <input
                type="text"
                name="name"
                placeholder="Enter group name"
                value={groupFormData.name}
                onChange={handleGroupInputChange}
                style={getInputStyle()}
              />
            </div>

            <div>
              <label style={getLabelStyle()}>Description</label>
              <textarea
                name="description"
                placeholder="Optional description"
                value={groupFormData.description}
                onChange={handleGroupInputChange}
                style={{ ...getTextareaStyle(), height: "60px" }}
              />
            </div>

            {/* Group Frequency */}
            <FrequencyInput
              frequencyType={groupFormData.frequencyType}
              frequencyValue={groupFormData.frequencyValue}
              onTypeChange={handleGroupFrequencyTypeChange}
              onValueChange={handleGroupFrequencyValueChange}
              inputStyle={getInputStyle()}
            />

            <div>
              <label style={getLabelStyle()}>
                Cooldown (Days)
              </label>
              <input
                type="number"
                name="cooldownDays"
                min="0"
                max="365"
                value={groupFormData.cooldownDays}
                onChange={handleGroupInputChange}
                style={getInputStyle()}
              />
              <small style={{ color: "#888", fontSize: "12px" }}>
                Minimum days before the same notification can be picked again within this group. Set to 0 for no cooldown.
              </small>
            </div>

            <div>
              <label style={getLabelStyle()}>Status *</label>
              <select
                name="isActive"
                value={groupFormData.isActive ? "true" : "false"}
                onChange={(e) => setGroupFormData(prev => ({ ...prev, isActive: e.target.value === "true" }))}
                style={getInputStyle()}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>



            <div>
              <label style={getLabelStyle()}>Custom Date</label>
              <input
                type="datetime-local"
                name="customDate"
                value={groupFormData.customDate}
                onChange={handleGroupInputChange}
                style={getInputStyle()}
              />
            </div>

            {/* Test mode for group */}
            {groupFormData.isTest && (
              <div style={{
                padding: "12px",
                backgroundColor: "#fff3cd",
                borderRadius: "5px",
                border: "1px solid #ffc107",
                marginBottom: "10px",
              }}>
                <label style={{ ...getLabelStyle(), color: "#856404" }}>
                  Test User ID *
                </label>
                <input
                  type="text"
                  name="testUserId"
                  placeholder="Enter MongoDB userId"
                  value={groupFormData.testUserId}
                  onChange={handleGroupInputChange}
                  style={{ ...getInputStyle(), marginBottom: "0", borderColor: "#ffc107" }}
                />
                <small style={{ color: "#856404", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  Only this user will receive group notifications
                </small>
              </div>
            )}

            <div style={getButtonGroupStyle()}>
              <button
                onClick={handleGroupSave}
                disabled={savingGroup}
                style={{ ...getButtonStyle('success'), backgroundColor: "#28a745", color: "white" }}
              >
                {savingGroup ? 'Saving...' : (editingGroup ? 'Update' : 'Create')}
              </button>
              <button
                onClick={() => toggleGroupModal()}
                style={{ ...getButtonStyle('danger'), backgroundColor: "#dc3545", color: "white" }}
              >
                Cancel
              </button>
              <button
                onClick={handleGroupTestToggle}
                style={{
                  ...getButtonStyle('test'),
                  backgroundColor: groupFormData.isTest ? "#ffc107" : "#6c757d",
                  color: groupFormData.isTest ? "#333" : "white",
                  marginLeft: isMobile ? "0" : "auto",
                  fontWeight: groupFormData.isTest ? "600" : "normal",
                }}
              >
                {groupFormData.isTest ? "Test On" : "Test"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================== */}
      {/* TEST INFO MODAL */}
      {/* ======================== */}
      {showTestInfoModal && (
        <div style={getPopupContainerStyle()}>
          <div style={{ ...getPopupStyle(), width: isMobile ? "100%" : "600px", maxWidth: "600px" }}>
            <h4 style={{ fontSize: isMobile ? "16px" : "18px", marginBottom: "15px", textAlign: "center", color: "#333" }}>
              Test Mode Logic Explained
            </h4>
            
            <p style={{ fontSize: "14px", color: "#555", marginBottom: "15px", lineHeight: "1.5" }}>
              When testing notifications, the system uses specific rules to determine which user receives the test based on how the send is triggered.
            </p>

            <div style={{ overflowX: "auto", marginBottom: "20px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", textAlign: "left" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                    <th style={{ padding: "10px", border: "1px solid #dee2e6" }}>Action Triggered</th>
                    <th style={{ padding: "10px", border: "1px solid #dee2e6" }}>Test Settings Used</th>
                    <th style={{ padding: "10px", border: "1px solid #dee2e6" }}>Explanation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: "10px", border: "1px solid #dee2e6", fontWeight: "bold" }}>
                      Automatic Cron Job<br />
                      <span style={{ fontWeight: "normal", fontSize: "11px", color: "#666" }}>(Group is due)</span>
                    </td>
                    <td style={{ padding: "10px", border: "1px solid #dee2e6", color: "#6f42c1", fontWeight: "bold" }}>
                      Group's Test Settings
                    </td>
                    <td style={{ padding: "10px", border: "1px solid #dee2e6" }}>
                      The group's test setting completely overrides the individual notification's setting.
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: "10px", border: "1px solid #dee2e6", fontWeight: "bold" }}>
                      "Send Random"<br />
                      <span style={{ fontWeight: "normal", fontSize: "11px", color: "#666" }}>(Group action button)</span>
                    </td>
                    <td style={{ padding: "10px", border: "1px solid #dee2e6", color: "#6f42c1", fontWeight: "bold" }}>
                      Group's Test Settings
                    </td>
                    <td style={{ padding: "10px", border: "1px solid #dee2e6" }}>
                      Sends a random notification to the Group's designated test user. Overrides individual settings.
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: "10px", border: "1px solid #dee2e6", fontWeight: "bold" }}>
                      Individual "Send"<br />
                      <span style={{ fontWeight: "normal", fontSize: "11px", color: "#666" }}>(Notification action button)</span>
                    </td>
                    <td style={{ padding: "10px", border: "1px solid #dee2e6", color: "#007bff", fontWeight: "bold" }}>
                      Notification's Test Settings
                    </td>
                    <td style={{ padding: "10px", border: "1px solid #dee2e6" }}>
                      Bypasses the group entirely. Sends using the notification's own test settings.
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: "10px", border: "1px solid #dee2e6", fontWeight: "bold" }}>
                      Standalone Notification<br />
                      <span style={{ fontWeight: "normal", fontSize: "11px", color: "#666" }}>(Not in a group)</span>
                    </td>
                    <td style={{ padding: "10px", border: "1px solid #dee2e6", color: "#007bff", fontWeight: "bold" }}>
                      Notification's Test Settings
                    </td>
                    <td style={{ padding: "10px", border: "1px solid #dee2e6" }}>
                      Uses its own test settings normally via cron job or manual send.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ textAlign: "center" }}>
              <button
                onClick={() => setShowTestInfoModal(false)}
                style={{ ...getButtonStyle('secondary'), backgroundColor: "#6c757d", color: "white", padding: "8px 24px" }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================== */}
      {/* CONTENT */}
      {/* ======================== */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", fontSize: "18px", color: "#666" }}>
          Loading notifications...
        </div>
      ) : (
        <>
          {/* ======================== */}
          {/* GROUPS SECTION */}
          {/* ======================== */}
          {groups.length > 0 && (
            <div style={{ marginBottom: "30px" }}>
              <h5 style={{ fontSize: "18px", marginBottom: "15px", color: "#6f42c1", display: "flex", alignItems: "center", gap: "8px" }}>
                <span>📁</span> Notification Groups
              </h5>
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: "15px"
              }}>
                {groups.map((group) => (
                  <div key={group._id} style={{
                    background: "white",
                    border: group.isTest ? "2px solid #ffc107" : "2px solid #6f42c1",
                    borderRadius: "8px",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    overflow: "hidden"
                  }}>
                    {/* Group Header */}
                    <div
                      style={{
                        padding: "15px 20px",
                        backgroundColor: "#f8f5ff",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        cursor: "pointer",
                        flexWrap: "wrap",
                        gap: "10px"
                      }}
                      onClick={() => toggleGroupExpand(group._id)}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>
                        <span style={{ fontSize: "16px", transition: "transform 0.2s", transform: expandedGroups[group._id] ? "rotate(90deg)" : "rotate(0deg)" }}>
                          ▶
                        </span>
                        <h4 style={{ margin: 0, color: "#333" }}>{group.name}</h4>
                        {/* Status badge */}
                        <div style={{
                          backgroundColor: group.isActive ? "#28a745" : "#dc3545",
                          color: "white",
                          padding: "2px 8px",
                          borderRadius: "12px",
                          fontSize: "11px",
                          fontWeight: "600",
                        }}>
                          {group.isActive ? "Active" : "Inactive"}
                        </div>
                        {group.isTest && (
                          <div style={{
                            backgroundColor: "#ffc107",
                            color: "#333",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            fontSize: "11px",
                            fontWeight: "600",
                          }}>
                            Test
                          </div>
                        )}
                        <span style={{ color: "#888", fontSize: "13px" }}>
                          {group.notifications?.length || 0} notification{(group.notifications?.length || 0) !== 1 ? 's' : ''}
                        </span>
                        <span style={{ color: "#888", fontSize: "13px" }}>
                          • {formatFrequencyLabel(group.frequency)}
                        </span>
                        {group.cooldownDays > 0 && (
                          <span style={{ color: "#888", fontSize: "13px" }}>
                            • Cooldown: {group.cooldownDays}d
                          </span>
                        )}
                        {group.lastSentAt && (
                          <span style={{ color: "#888", fontSize: "12px" }}>
                            • Last sent: {formatDate(group.lastSentAt)}
                          </span>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: "5px" }} onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleGroupSend(group._id)}
                          disabled={sendingGroupId === group._id}
                          title="Send Random"
                          style={getIconButton("#28a745", sendingGroupId === group._id)}
                        >
                          <FontAwesomeIcon icon={sendingGroupId === group._id ? faSpinner : faRandom} spin={sendingGroupId === group._id} />
                        </button>
                        <button
                          onClick={() => toggleGroupModal(group)}
                          title="Edit Group"
                          style={getIconButton("#007bff")}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          onClick={() => handleGroupDelete(group._id)}
                          title="Delete Group"
                          style={getIconButton("#dc3545")}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>

                    {/* Group Expanded Content — member notifications */}
                    {expandedGroups[group._id] && (
                      <div style={{ padding: "15px 20px", borderTop: "1px solid #e8e0f5" }}>
                        {group.description && (
                          <p style={{ color: "#666", marginBottom: "15px", fontSize: "14px" }}>{group.description}</p>
                        )}

                        {(!group.notifications || group.notifications.length === 0) ? (
                          <div style={{ textAlign: "center", padding: "20px", color: "#999", fontSize: "14px" }}>
                            No notifications in this group yet. Create one and assign it to this group.
                          </div>
                        ) : (
                          <div style={{
                            display: "grid",
                            gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(300px, 1fr))",
                            gap: "12px"
                          }}>
                            {group.notifications.map((notif) => (
                              <div key={notif._id} style={{
                                background: "#fafafa",
                                border: "1px solid #e0e0e0",
                                borderRadius: "6px",
                                padding: "12px",
                              }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                                  <h5 style={{ margin: 0, fontSize: "14px", color: "#333" }}>{notif.title}</h5>
                                  <div style={{ display: "flex", gap: "4px" }}>
                                    <div style={{
                                      backgroundColor: notif.isActive ? "#28a745" : "#dc3545",
                                      color: "white",
                                      padding: "1px 6px",
                                      borderRadius: "10px",
                                      fontSize: "10px",
                                      fontWeight: "600",
                                    }}>
                                      {notif.isActive ? "Active" : "Inactive"}
                                    </div>
                                  </div>
                                </div>
                                <p style={{ margin: "0 0 8px 0", fontSize: "13px", color: "#666", lineHeight: "1.3" }}>{notif.text}</p>
                                {notif.image && (
                                  <img src={notif.image} alt="Notification" style={{ maxWidth: "100%", maxHeight: "80px", borderRadius: "4px", marginBottom: "8px", objectFit: "cover" }} />
                                )}
                                <div style={{ marginBottom: "8px" }}>
                                  <span style={{ fontSize: "13px", color: "#888", display: "flex", alignItems: "center", gap: "4px" }}>
                                    Freq: {formatFrequencyLabel(notif.duration)}
                                    <span title="Overridden by group frequency" style={{ cursor: "help", color: "#6f42c1", fontSize: "12px", fontWeight: "bold" }}>ⓘ</span>
                                  </span>
                                  {notif.customDate && (
                                    <span style={{ fontSize: "13px", color: "#888", display: "flex", alignItems: "center", gap: "4px" }}>
                                      Date: {formatDate(notif.customDate)}
                                      <span title="Overridden by group date" style={{ cursor: "help", color: "#6f42c1", fontSize: "12px", fontWeight: "bold" }}>ⓘ</span>
                                    </span>
                                  )}
                                </div>
                                <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                                  <button
                                    onClick={() => handleSend(notif._id)}
                                    disabled={sendingId === notif._id}
                                    title="Send Now"
                                    style={getIconButton("#28a745", sendingId === notif._id)}
                                  >
                                    <FontAwesomeIcon icon={sendingId === notif._id ? faSpinner : faPaperPlane} spin={sendingId === notif._id} />
                                  </button>
                                  <button
                                    onClick={() => togglePopup(notif)}
                                    title="Edit Notification"
                                    style={getIconButton("#007bff")}
                                  >
                                    <FontAwesomeIcon icon={faEdit} />
                                  </button>
                                  <button
                                    onClick={() => handleRemoveFromGroup(notif._id, group._id)}
                                    title="Remove from Group"
                                    style={getIconButton("#fd7e14")}
                                  >
                                    <FontAwesomeIcon icon={faMinusCircle} />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(notif._id)}
                                    title="Delete Notification"
                                    style={getIconButton("#dc3545")}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                </div>
                                {notif.lastSentAt && (
                                  <small style={{ color: "#aaa", fontSize: "11px", marginTop: "6px", display: "block" }}>
                                    Last sent: {formatDate(notif.lastSentAt)}
                                  </small>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ======================== */}
          {/* STANDALONE NOTIFICATIONS */}
          {/* ======================== */}
          <div>
            <h5 style={{ fontSize: "18px", marginBottom: "15px", color: "#333", display: "flex", alignItems: "center", gap: "8px" }}>
              <span>🔔</span> Standalone Notifications
            </h5>
            <div style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(350px, 1fr))",
              gap: "20px"
            }}>
              {standalonePopups.length === 0 ? (
                <div style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "#666",
                  fontSize: "16px",
                  gridColumn: "1 / -1"
                }}>
                  No standalone notifications found. Create your first notification!
                </div>
              ) : (
                standalonePopups.map((popup) => (
                  <div key={popup._id} style={{
                    background: "white",
                    border: popup.isTest ? "2px solid #ffc107" : "1px solid #ddd",
                    borderRadius: "8px",
                    padding: "20px",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    minHeight: "200px",
                    position: "relative",
                  }}>
                    <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "15px",
                      flexDirection: isMobile ? "column" : "row",
                      gap: isMobile ? "10px" : "0"
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, paddingRight: "15px" }}>
                        <h3 style={{ margin: 0, color: "#333" }}>{popup.title}</h3>
                        {/* Test badge moved next to title */}
                        {popup.isTest && (
                          <div style={{
                            backgroundColor: "#ffc107",
                            color: "#333",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            whiteSpace: "nowrap"
                          }}>
                            Test
                          </div>
                        )}
                        {/* Active/Inactive badge */}
                        <div style={{
                          backgroundColor: popup.isActive ? "#28a745" : "#dc3545",
                          color: "white",
                          padding: "2px 8px",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: "600",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          whiteSpace: "nowrap"
                        }}>
                          {popup.isActive ? "Active" : "Inactive"}
                        </div>
                      </div>
                      <div style={{
                        display: "flex",
                        gap: "5px",
                        flexWrap: "wrap",
                        alignSelf: isMobile ? "flex-end" : "flex-start"
                      }}>
                        <button
                          onClick={() => handleSend(popup._id)}
                          disabled={sendingId === popup._id}
                          title="Send Now"
                          style={getIconButton("#28a745", sendingId === popup._id)}
                        >
                          <FontAwesomeIcon icon={sendingId === popup._id ? faSpinner : faPaperPlane} spin={sendingId === popup._id} />
                        </button>
                        <button
                          onClick={() => togglePopup(popup)}
                          title="Edit Notification"
                          style={getIconButton("#007bff")}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        {/* Add to Group custom dropdown */}
                        {groups.length > 0 && (
                          <div style={{ position: "relative", display: "inline-block" }}>
                            <button
                              title="Add to Group"
                              onClick={() => setActiveGroupMenuId(activeGroupMenuId === popup._id ? null : popup._id)}
                              style={{
                                ...getIconButton("#6f42c1"),
                                backgroundColor: activeGroupMenuId === popup._id ? "#f0ebfa" : "transparent",
                                borderRadius: "4px"
                              }}
                            >
                              <FontAwesomeIcon icon={faFolderPlus} />
                            </button>

                            {activeGroupMenuId === popup._id && (
                              <>
                                {/* Click outside backdrop */}
                                <div
                                  onClick={() => setActiveGroupMenuId(null)}
                                  style={{
                                    position: "fixed",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    zIndex: 999
                                  }}
                                />

                                {/* Custom Floating Menu */}
                                <div style={{
                                  position: "absolute",
                                  top: "100%",
                                  right: 0,
                                  marginTop: "6px",
                                  width: "220px",
                                  backgroundColor: "#ffffff",
                                  borderRadius: "8px",
                                  boxShadow: "0 8px 24px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.06)",
                                  border: "1px solid #e9e4f5",
                                  padding: "6px 0",
                                  zIndex: 1000,
                                  textAlign: "left"
                                }}>
                                  <div style={{
                                    padding: "6px 12px",
                                    fontSize: "11px",
                                    fontWeight: "700",
                                    color: "#6f42c1",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                    borderBottom: "1px solid #f0ecf8",
                                    marginBottom: "4px"
                                  }}>
                                    Select Target Group
                                  </div>
                                  <div style={{ maxHeight: "180px", overflowY: "auto" }}>
                                    {groups.map(g => (
                                      <div
                                        key={g._id}
                                        onClick={() => {
                                          handleAddToGroup(popup._id, g._id);
                                          setActiveGroupMenuId(null);
                                        }}
                                        style={{
                                          padding: "8px 12px",
                                          fontSize: "13px",
                                          color: "#333",
                                          cursor: "pointer",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "space-between",
                                          gap: "8px",
                                          transition: "background-color 0.15s ease"
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f5f2fa"}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                                      >
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
                                          <FontAwesomeIcon icon={faFolder} style={{ color: "#6f42c1", fontSize: "12px" }} />
                                          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: "500" }}>
                                            {g.name}
                                          </span>
                                        </div>
                                        <span style={{
                                          fontSize: "10px",
                                          backgroundColor: "#eee8f7",
                                          color: "#6f42c1",
                                          padding: "2px 6px",
                                          borderRadius: "10px",
                                          fontWeight: "600",
                                          whiteSpace: "nowrap"
                                        }}>
                                          {g.notifications?.length || 0}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                        <button
                          onClick={() => handleDelete(popup._id)}
                          title="Delete Notification"
                          style={getIconButton("#dc3545")}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>

                    <div style={{ color: "#666" }}>
                      <p style={{ marginBottom: "15px", lineHeight: "1.4" }}>{popup.text}</p>

                      {/* Image thumbnail */}
                      {popup.image && (
                        <div style={{ marginBottom: "15px" }}>
                          <img
                            src={popup.image}
                            alt="Notification"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "120px",
                              borderRadius: "5px",
                              border: "1px solid #ddd",
                              objectFit: "cover",
                            }}
                          />
                        </div>
                      )}

                      <div style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "5px",
                        marginBottom: "15px"
                      }}>
                        <span style={{ fontSize: isMobile ? "13px" : "14px", color: "#888" }}>
                          Frequency: {formatFrequencyLabel(popup.duration)}
                        </span>
                        <span style={{ fontSize: isMobile ? "13px" : "14px", color: "#888" }}>
                          Route: {popup.route}
                        </span>
                        {popup.isTest && popup.testUserId && (
                          <span style={{ fontSize: isMobile ? "13px" : "14px", color: "#856404" }}>
                            Test User: {popup.testUserId}
                          </span>
                        )}
                        {popup.customDate && (
                          <span style={{ fontSize: isMobile ? "13px" : "14px", color: "#888" }}>
                            Date: {formatDate(popup.customDate)}
                          </span>
                        )}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                        <small style={{ color: "#aaa", fontSize: "12px" }}>
                          Created: {formatDate(popup.createdAt)}
                        </small>
                        <small style={{ color: "#aaa", fontSize: "12px" }}>
                          Updated: {formatDate(popup.updatedAt)}
                        </small>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AppManagement;